import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { COLORS } from '../lib/theme'
import { useHasNavDrawer } from '../lib/useNavDrawer'
import { Icon } from './Icon'
import { MegaMenu, type MegaMenuColumn, type MegaMenuLabels } from './MegaMenu'
import type { NavSection } from './Sidebar'
import { IndexMenu, type IndexMenuLabels } from './topbar/IndexMenu'
import { AccountMenu, type AccountMenuItem, type AccountMenuUser } from './topbar/AccountMenu'
import { CartDrawer, type CartDrawerLabels, type CartLine } from './topbar/CartDrawer'
import { FlagIcon, type CountryCode } from './topbar/FlagIcon'
import {
  DEFAULT_LANGUAGES,
  LocationModal,
  type LocaleSelection,
  type LocaleOption,
  type LocationModalLabels,
} from './topbar/LocationModal'
import { SearchPanel, type SearchPanelLabels } from './topbar/SearchPanel'
import { ICON_BTN, OVERLAY_STYLE, PILL, Z, iconButtonStyle, isInAppHref, useIsMobileTopBar } from './topbar/topbarChrome'

export interface TopBarAriaLabels {
  openNav?: string
  openMenu?: string
  closeMenu?: string
  search?: string
  notifications?: string
  cart?: string
  account?: string
  locale?: string
}

/** Which surface each control opens. One at a time, as on wehunt.app. */
type Panel = 'menu' | 'search' | 'cart' | 'account' | 'locale'

export interface TopBarProps {
  onSidebarToggle?: () => void
  /**
   * `minimal` is the wordmark-only bar viltrapport ships. `full` is the bar
   * from the WeHunt Web prototype — search, account, cart, locale and the CTA —
   * matching the public header on wehunt.app. Default stays `minimal` so
   * turning the full bar on is a per-shell decision rather than something
   * every consumer inherits.
   */
  variant?: 'minimal' | 'full'
  /**
   * `solid` paints the bar's own band in the app surface — the webapp shell,
   * where the pill sits above the content. `transparent` drops that band so the
   * pill floats over whatever is beneath it, which is what wehunt.app does over
   * its hero photography. The bar keeps its place in the flow either way.
   */
  surface?: 'solid' | 'transparent'
  showCta?: boolean
  showSearch?: boolean
  showCart?: boolean
  showAccount?: boolean
  showLocale?: boolean
  logo?: string
  /**
   * Variant shown while the dark theme is active. Supply this when `logo` is
   * inked for light surfaces only; both are rendered and CSS picks one, because
   * the theme lives as a `data-theme` attribute on the document element (see
   * lib/themeMode). Omit it to render `logo` alone in both themes.
   */
  logoDark?: string
  logoAlt?: string
  logoHref?: string
  ariaLabels?: TopBarAriaLabels
  /** Optional node rendered in the right-side group, before the CTA. */
  slotRight?: ReactNode

  ctaLabel?: string
  ctaHref?: string
  onCtaClick?: () => void

  /** Profile shown in the account menu. Omit to make the icon a plain link. */
  user?: AccountMenuUser
  accountItems?: AccountMenuItem[]
  accountHref?: string
  onNavigate?: (href: string) => void

  /**
   * The cart lives in the shop, on another domain, and this app cannot read
   * it. A shell that knows nothing of its contents passes `cartHref` and the
   * icon becomes a plain link there; a shell that has the lines (the prototype)
   * passes them and gets the dropdown.
   */
  cartHref?: string
  cartLines?: CartLine[]
  cartLabels?: CartDrawerLabels

  onSearchSubmit?: (query: string) => void
  searchLabels?: SearchPanelLabels

  menuColumns?: MegaMenuColumn[]
  menuLabels?: MegaMenuLabels

  /**
   * The account rail. Supplying it turns the menu into the combined index —
   * the rail as the first section, the marketing columns as the rest — and
   * retires the rail's own handle, at every width where the rail is off-canvas.
   * Omit it (the marketing shell does) and the menu stays the columns alone.
   */
  navSections?: NavSection[]
  /** Which rail row to mark active. Required with `navSections`. */
  currentPath?: string
  indexMenuLabels?: IndexMenuLabels
  /** Sign-out, which moves into the index menu with the rest of the rail. */
  onSignOut?: () => void
  /**
   * Routes a menu/search link *into this app* instead of letting the browser
   * follow it; absolute URLs are always left to the browser.
   */
  onNavigateTo?: (href: string) => void

  locale?: LocaleSelection
  countries?: LocaleOption[]
  languages?: LocaleOption[]
  /** Hide the country picker in a shell that has no notion of a market. */
  showLocaleCountry?: boolean
  onLocaleChange?: (next: LocaleSelection) => void
  localeLabels?: LocationModalLabels
}

// `display` is deliberately absent: it lives on `.wh-logo` in components.css so
// the theme swap can override it. An inline `display: block` here would outrank
// every stylesheet rule and both variants would render stacked.
function logoImgStyle(height: number) {
  return { height, width: 'auto', maxWidth: '100%' } as const
}

function IconButton({
  name,
  label,
  badge,
  mobile,
  expanded,
  onClick,
  href,
  children,
}: {
  name?: string
  label: string
  badge?: number
  mobile: boolean
  expanded?: boolean
  onClick?: () => void
  href?: string
  children?: ReactNode
}) {
  const style = iconButtonStyle(mobile)
  const iconSize = mobile ? ICON_BTN.iconMobile : ICON_BTN.icon
  const body = (
    <>
      {children ?? (name && <Icon name={name} size={iconSize} />)}
      {!!badge && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 2,
            right: 0,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            borderRadius: 999,
            background: '#ff681f',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${COLORS.secondary}`,
          }}
        >
          {badge}
        </span>
      )}
    </>
  )

  // Hover lift is applied by hand rather than by a class so the button keeps
  // working in shells that do not load components.css.
  const hover = {
    onMouseEnter: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.08)'
    },
    onMouseLeave: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.backgroundColor = 'transparent'
    },
  }

  if (href) {
    return (
      <a href={href} aria-label={label} style={style} {...hover}>
        {body}
      </a>
    )
  }
  return (
    <button type="button" aria-label={label} aria-expanded={expanded} onClick={onClick} style={style} {...hover}>
      {body}
    </button>
  )
}

export function TopBar({
  onSidebarToggle,
  variant = 'minimal',
  surface = 'solid',
  showCta = true,
  showSearch = true,
  showCart = true,
  showAccount = true,
  showLocale = true,
  logo = '/assets/wehunt-logo-on-light.svg',
  logoDark,
  logoAlt = 'WeHunt',
  logoHref = '/',
  ariaLabels,
  slotRight,
  ctaLabel = 'Try WeHunt free',
  ctaHref,
  onCtaClick,
  user,
  accountItems,
  accountHref,
  onNavigate,
  cartHref,
  cartLines = [],
  cartLabels,
  onSearchSubmit,
  searchLabels,
  menuColumns,
  menuLabels,
  navSections,
  currentPath = '/',
  indexMenuLabels,
  onSignOut,
  onNavigateTo,
  locale = { country: 'GLOBAL', language: 'en' },
  countries,
  languages,
  showLocaleCountry = true,
  onLocaleChange,
  localeLabels,
}: TopBarProps) {
  const full = variant === 'full'
  const mobile = useIsMobileTopBar()
  // Wider than `mobile` — the rail is a drawer from 1024px down, while the
  // pill only goes compact at 768. Between the two the handle is needed but
  // the roomy pill sizing still applies.
  const drawerWidth = useHasNavDrawer()
  const [panel, setPanel] = useState<Panel | null>(null)
  const pillRef = useRef<HTMLDivElement>(null)

  const labels = {
    openNav: ariaLabels?.openNav ?? 'Open navigation',
    openMenu: ariaLabels?.openMenu ?? 'Open menu',
    closeMenu: ariaLabels?.closeMenu ?? 'Close menu',
    search: ariaLabels?.search ?? 'Search',
    cart: ariaLabels?.cart ?? 'Cart',
    account: ariaLabels?.account ?? 'Account',
    locale: ariaLabels?.locale ?? 'Select country and language',
  }

  const close = useCallback(() => setPanel(null), [])
  const toggle = useCallback((next: Panel) => setPanel((p) => (p === next ? null : next)), [])

  // The cart and account surfaces are dropdowns, not modals — nothing dims
  // behind them, so an outside click is what dismisses them.
  useEffect(() => {
    if (panel !== 'cart' && panel !== 'account') return
    const onDown = (e: MouseEvent) => {
      if (!pillRef.current?.contains(e.target as Node)) close()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [panel, close])

  // A resize across either breakpoint changes the menu under the pointer: the
  // 768 one rebinds the toggle, and the 1024 one swaps the marketing columns
  // for the combined index and back. Dismissing on both means a panel is only
  // ever the one the width it opened at asked for.
  useEffect(() => close(), [mobile, drawerWidth, close])

  const languageFlag: CountryCode =
    (languages ?? DEFAULT_LANGUAGES).find((l) => l.id === locale.language)?.flag ?? 'GB'

  const menuOpen = panel === 'menu'
  const dimmed = menuOpen || panel === 'search'
  /** A shell with no account rail passes no toggle handler. */
  const hasDrawer = !!onSidebarToggle

  /**
   * A 375px pill fits the CTA or the locale flag, not both — with both, the
   * button overhangs the pill's right edge by ~25px. Where the CTA is the only
   * route back into the webapp (a marketing page, which has no rail) it wins,
   * and the country picker — which changes nothing you cannot change from a
   * desktop — gives up its place.
   */
  const localeYieldsToCta = full && mobile && !hasDrawer && showCta

  /**
   * The combined index menu, which applies wherever the rail is off-canvas and
   * the shell has handed its rail over. That condition is exactly the one that
   * used to put two menu triggers in the same corner — the rail's handle and
   * the marketing hamburger — so it is the condition under which the design
   * replaces them with one. Above it the rail is on screen and the hamburger is
   * the marketing menu alone, as on wehunt.app.
   */
  const indexMode = full && drawerWidth && !!navSections?.length

  /**
   * In the full bar the hamburger is the marketing menu at every width — except
   * in `indexMode`, where the same control opens the marketing tree *and* the
   * rail. Either way it is one control opening one panel; it never toggles the
   * drawer. In the minimal bar it is the drawer handle it has always been.
   */
  const onToggleClick = full ? () => toggle('menu') : () => onSidebarToggle?.()

  /** The cart is a link when the shell can only point at the shop's cart. */
  const cartAsLink = cartHref !== undefined && cartLines.length === 0

  return (
    <header
      style={{
        background: surface === 'transparent' ? 'transparent' : COLORS.surface,
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        // Raised over the backdrop only while a panel is open, so the bar does
        // not otherwise outrank the app's own dialogs.
        zIndex: dimmed ? Z.pill : 30,
      }}
    >
      {dimmed && <div style={OVERLAY_STYLE} onClick={close} />}

      {/* Two boxes, mirroring the site's header > pillWrapper: the outer one
          owns the page gutter and centres, the inner one caps the width.

          The full bar caps at PILL.maxWidth — the same 716px wehunt.app uses —
          so the signed-in pill is the same object as the public one rather than
          a wider variant of it. It is deliberately NOT aligned to the content
          column: the site centres the pill over the page.

          The minimal bar keeps mirroring wh-app-inner (maxWidth 1440, 24px
          gutter) so its edges stay aligned with the sidebar and main column. */}
      <div
        style={{
          maxWidth: full ? undefined : 1440,
          width: '100%',
          margin: '0 auto',
          padding: mobile && full ? '0 16px' : '0 24px',
          boxSizing: 'border-box',
          display: full ? 'flex' : undefined,
          justifyContent: full ? 'center' : undefined,
          position: 'relative',
          zIndex: dimmed ? Z.pill : undefined,
        }}
      >
        <div style={{ position: 'relative', width: '100%', maxWidth: full ? PILL.maxWidth : undefined }}>
        <div
          ref={pillRef}
          style={{
            position: 'relative',
            background: COLORS.secondary,
            color: COLORS.onSecondary,
            borderRadius: PILL.radius,
            boxShadow: '0 6px 20px rgba(20,40,20,0.14), 0 1px 0 rgba(255,255,255,0.05) inset',
            display: 'flex',
            alignItems: 'center',
            justifyContent: full ? 'space-between' : undefined,
            padding: full ? (mobile ? PILL.paddingMobile : PILL.padding) : '8px 10px 8px 14px',
            height: full && mobile ? PILL.heightMobile : PILL.height,
            boxSizing: 'border-box',
            gap: full ? (mobile ? PILL.gapMobile : PILL.gap) : 8,
          }}
        >
          {/* Left group */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: full ? (mobile ? PILL.leftGapMobile : PILL.leftGap) : 0,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            {/* The account rail's handle. Only below the breakpoint, where the
                rail has collapsed into a drawer — above it the rail is on screen
                and a control to reveal it would do nothing. Left-most because
                that is the edge the drawer slides in from, and a panel glyph
                rather than a second set of bars so it cannot be mistaken for the
                marketing menu beside it.

                Gone in `indexMode`: the rail is inside the hamburger's panel
                there, so this would be a second door onto the same room. */}
            {full && drawerWidth && hasDrawer && !indexMode && (
              <button
                type="button"
                onClick={() => onSidebarToggle?.()}
                aria-label={labels.openNav}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  background: 'transparent',
                  color: COLORS.onSecondaryFixed,
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <Icon name="sidebar" size={24} />
              </button>
            )}

            <button
              type="button"
              onClick={onToggleClick}
              aria-label={full ? (menuOpen ? labels.closeMenu : labels.openMenu) : labels.openNav}
              aria-expanded={full ? menuOpen : undefined}
              // In the minimal bar the toggle is a mobile-only affordance and
              // the stylesheet hides it above the breakpoint; the full bar
              // carries it at every width, as the site does.
              className={full ? undefined : 'wh-topbar-sidebar-btn'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: full ? 24 : 40,
                height: full ? 24 : 40,
                flexShrink: 0,
                borderRadius: full ? 0 : 999,
                background: 'transparent',
                color: full ? COLORS.onSecondaryFixed : COLORS.onSecondaryContainer,
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              <Icon name={full ? (menuOpen ? 'close' : 'menu') : 'sidebar'} size={full ? 24 : 22} />
            </button>

            {/* Logo. With a dark variant supplied, both images sit in the DOM and
                the stylesheet reveals one per theme, so the link owns the
                accessible name and the images become decorative — otherwise a
                screen reader announces the wordmark twice. */}
            <a
              href={logoHref}
              aria-label={logoDark ? logoAlt : undefined}
              // Routed in-app when the shell supplies a router, so clicking the
              // wordmark lands on the app's home without a full reload.
              onClick={
                onNavigateTo && isInAppHref(logoHref)
                  ? (e) => {
                      e.preventDefault()
                      onNavigateTo(logoHref)
                    }
                  : undefined
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: full ? 0 : 16,
                minWidth: 0,
              }}
            >
              {(() => {
                const h = full ? (mobile ? PILL.logoHeightMobile : PILL.logoHeight) : 26
                // The minimal bar keeps the 120x26 intrinsic-size hint it has
                // always carried, so the shells already shipping it are byte
                // identical. The full bar's height varies with the breakpoint,
                // where a fixed pair would be wrong, and the inline height plus
                // width:auto already reserve the box before load.
                const hint = full ? {} : { width: 120, height: 26 }
                return (
                  <>
                    <img
                      src={logo}
                      alt={logoDark ? '' : logoAlt}
                      aria-hidden={logoDark ? true : undefined}
                      className={logoDark ? 'wh-logo wh-logo-on-light' : 'wh-logo'}
                      {...hint}
                      style={logoImgStyle(h)}
                    />
                    {logoDark && (
                      <img
                        src={logoDark}
                        alt=""
                        aria-hidden
                        className="wh-logo wh-logo-on-dark"
                        {...hint}
                        style={logoImgStyle(h)}
                      />
                    )}
                  </>
                )
              })()}
            </a>
          </div>

          {/* Right group */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: full ? 0 : 4,
              flex: full ? undefined : 1,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            {slotRight}

            {full && showSearch && (
              <IconButton
                name="search"
                label={labels.search}
                mobile={mobile}
                expanded={panel === 'search'}
                onClick={() => toggle('search')}
              />
            )}

            {full && showAccount && (
              user || accountItems?.length ? (
                <IconButton
                  name="account"
                  label={labels.account}
                  mobile={mobile}
                  expanded={panel === 'account'}
                  onClick={() => toggle('account')}
                />
              ) : (
                <IconButton name="account" label={labels.account} mobile={mobile} href={accountHref ?? '/account'} />
              )
            )}

            {full && showCart && (
              cartAsLink ? (
                <IconButton name="cart" label={labels.cart} mobile={mobile} href={cartHref} />
              ) : (
                <IconButton
                  name="cart"
                  label={labels.cart}
                  badge={cartLines.length || undefined}
                  mobile={mobile}
                  expanded={panel === 'cart'}
                  onClick={() => toggle('cart')}
                />
              )
            )}

            {full && showLocale && !localeYieldsToCta && (
              <button
                type="button"
                onClick={() => toggle('locale')}
                aria-label={labels.locale}
                aria-expanded={panel === 'locale'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: 4,
                  flexShrink: 0,
                  borderRadius: 999,
                  border: 'none',
                  background: 'transparent',
                  color: COLORS.onSecondaryFixed,
                  cursor: 'pointer',
                }}
              >
                <FlagIcon code={languageFlag} size={20} />
              </button>
            )}

            {/* The site drops the CTA on a phone, where its account icon is
                still the way in. This bar has no account icon, so hiding it out
                on the marketing pages — which have no rail either — would strand
                you there with no route back into the webapp. So it goes only
                where something else already leads inward: the webapp itself,
                which has the rail and where the button is redundant anyway. */}
            {showCta && !(full && drawerWidth && hasDrawer) && (() => {
              const ctaStyle = {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: full ? 8 : 6,
                flexShrink: 0,
                height: full ? undefined : 40,
                padding: full ? '8px 16px' : '0 18px',
                borderRadius: 999,
                background: COLORS.primary,
                color: COLORS.onPrimary,
                fontSize: full ? 14 : 13,
                fontWeight: full ? 500 : 700,
                lineHeight: full ? 1 : undefined,
                border: 'none',
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                cursor: 'pointer',
              } as const
              const className = full ? undefined : 'wh-topbar-cta'
              // A link when it goes somewhere, a button when it only runs a
              // handler — an `href="#"` would be announced as a link to nowhere.
              return ctaHref ? (
                <a href={ctaHref} onClick={onCtaClick} className={className} style={ctaStyle}>
                  {ctaLabel}
                </a>
              ) : (
                <button type="button" onClick={onCtaClick} className={className} style={ctaStyle}>
                  {ctaLabel}
                </button>
              )
            })()}
          </div>

          {full && (
            <>
              {!cartAsLink && (
                <CartDrawer open={panel === 'cart'} onClose={close} lines={cartLines} labels={cartLabels} />
              )}
              <AccountMenu
                open={panel === 'account'}
                onClose={close}
                user={user}
                items={accountItems ?? []}
                onNavigate={onNavigate}
              />
            </>
          )}
        </div>
        </div>
      </div>

      {full && menuOpen &&
        (indexMode ? (
          <IndexMenu
            onClose={close}
            navSections={navSections}
            currentPath={currentPath}
            columns={menuColumns}
            labels={indexMenuLabels}
            mobile={mobile}
            onNavigateTo={onNavigateTo}
            onSignOut={onSignOut}
          />
        ) : (
          <MegaMenu
            onClose={close}
            columns={menuColumns}
            labels={menuLabels}
            mobile={mobile}
            onNavigateTo={onNavigateTo}
          />
        ))}
      {full && (
        <SearchPanel
          open={panel === 'search'}
          onClose={close}
          onSubmit={(query) => {
            close()
            onSearchSubmit?.(query)
          }}
          labels={searchLabels}
          columns={menuColumns}
          mobile={mobile}
          onNavigateTo={onNavigateTo}
        />
      )}
      {full && (
        <LocationModal
          open={panel === 'locale'}
          onClose={close}
          selection={locale}
          countries={countries}
          languages={languages}
          showCountry={showLocaleCountry}
          onApply={onLocaleChange}
          labels={localeLabels}
        />
      )}
    </header>
  )
}
